import React, { useMemo, useState } from 'react';
import { Button, DatePicker, InputNumber, Input, Modal, Select, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { meName } from '../CustomerDemo/softphone';
import { useHr } from './hrStore';
import { getTp, tpApi, useTp, validateRequest, type ReqDraft } from './tpStore';
import { LEAVE_KIND, branchById, fmtD, fmtMoney, isWorking, leaveBalance, positionById, toDay, workingDaysBetween, type LeaveKind } from './hrLogic';
import { REQ_KIND, REQ_STATE, fmtMonth, otHours, shiftById, shiftLabel, type ReqKind, type ShiftDef, type TimeReq } from './tpLogic';
import { CONTRACT_KIND } from './hrLogic';
import type { PayPeriod, PayRow } from './payLogic';
import { Badge, Notes } from './hrUi';

export function useTpPerms() {
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const can = (k: string) => isSuperAdmin || hasPermission(k);
  return {
    canAssign: can('timekeeping.assign'), canApprove: can('timekeeping.approve'), canCreate: can('timekeeping.create'), canTimeEdit: can('timekeeping.edit'),
    canPayEdit: can('salary.edit'), canPayApprove: can('salary.approve'), canPayExport: can('salary.export'),
    me: meName() || 'Tôi',
  };
}

export const ShiftChip = ({ sh, extra, onClose, title }: { sh: ShiftDef; extra?: React.ReactNode; onClose?: () => void; title?: string }) => (
  <span title={title || `${sh.name} ${shiftLabel(sh)}`} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-tight ${sh.color}`}>
    <b>{sh.code}</b>{extra}
    {onClose && <button type="button" aria-label="Bỏ ca" className="opacity-60 hover:opacity-100" onClick={ev => { ev.stopPropagation(); onClose(); }}>×</button>}
  </span>
);

export const ReqKindTag = ({ kind }: { kind: ReqKind }) => <Badge className={REQ_KIND[kind].badge}>{REQ_KIND[kind].label}</Badge>;
export const ReqStateTag = ({ r }: { r: TimeReq }) => <Badge className={REQ_STATE[r.status].badge}>{REQ_STATE[r.status].label}</Badge>;

/** Chọn tháng dạng ‹ 10/2026 › */
export function MonthNav({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  const go = (n: number) => onChange(dayjs(`${month}-01`).add(n, 'month').format('YYYY-MM'));
  return (
    <div className="flex items-center gap-1">
      <Button size="middle" icon={<ChevronLeft size={14} />} onClick={() => go(-1)} aria-label="Tháng trước" />
      <DatePicker picker="month" allowClear={false} format="MM/YYYY" value={dayjs(`${month}-01`)} onChange={v => v && onChange(v.format('YYYY-MM'))} className="w-[120px]" />
      <Button size="middle" icon={<ChevronRight size={14} />} onClick={() => go(1)} aria-label="Tháng sau" />
    </div>
  );
}

export function BranchSelect({ value, onChange, all = true, className = 'w-[180px]' }: { value: string; onChange: (v: string) => void; all?: boolean; className?: string }) {
  const s = useHr();
  return <Select className={className} value={value} onChange={onChange} options={[...(all ? [{ value: '', label: 'Tất cả chi nhánh' }] : []), ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />;
}

/** Kết quả thao tác: lỗi → hộp thông báo, cảnh báo → message vàng */
export function done(r: { ok: boolean; errors?: string[]; warnings?: string[] }, ok?: string) {
  if (!r.ok) { Modal.error({ title: 'Chưa thực hiện được', content: <div className="space-y-1">{(r.errors || []).map(x => <div key={x}>• {x}</div>)}</div> }); return false; }
  if (ok) message.success(ok);
  r.warnings?.forEach(w => message.warning(w, 5));
  return true;
}

/* ============================== Tạo đơn ============================== */

export function RequestModal({ init, onClose, onSaved }: { init?: Partial<ReqDraft>; onClose: () => void; onSaved?: (id: string) => void }) {
  const s = useHr();
  const t = useTp();
  const { me } = useTpPerms();
  const today = toDay();
  const [d, setD] = useState<ReqDraft>({ kind: 'leave', leaveKind: 'annual', empId: '', reason: '', ...init } as ReqDraft);
  const patch = (p: Partial<ReqDraft>) => setD(x => ({ ...x, ...p }));
  const e = s.employees.find(x => x.id === d.empId);
  const emps = s.employees.filter(x => isWorking(x, today) || (x.leftDate && x.leftDate >= dayjs().subtract(40, 'day').format('YYYY-MM-DD')));
  const myShifts = useMemo(() => (d.empId ? t.assigns.filter(a => a.empId === d.empId && (d.kind === 'swap' ? a.date > today : d.kind === 'explain' ? a.date <= today && a.date >= dayjs().subtract(40, 'day').format('YYYY-MM-DD') : true)) : [])
    .sort((a, b) => b.date.localeCompare(a.date)).slice(0, 60), [t.assigns, d.empId, d.kind, today]);
  const days = d.kind === 'leave' && d.from && d.to && d.to >= d.from ? workingDaysBetween(d.from, d.to) : 0;
  const draft: ReqDraft = { ...d, ...(d.kind === 'leave' ? { days: d.days ?? days } : {}), date: d.kind === 'leave' ? undefined : d.date };
  const v = d.empId ? validateRequest(draft) : { errors: [], warnings: [] };
  const ot = d.kind === 'ot' && d.otFrom && d.otTo ? otHours(d.otFrom, d.otTo) : null;
  const pickAssign = (id: string) => { const a = t.assigns.find(x => x.id === id)!; patch({ assignId: id, date: a.date }); };
  const save = () => {
    const r = tpApi.createRequest(draft, me);
    if (done(r, 'Đã gửi đơn')) { onSaved?.(r.id!); onClose(); }
  };
  const lb = e && d.kind === 'leave' ? leaveBalance(s, e, d.from || today) : null;
  const time = (v2?: string) => (v2 ? dayjs(`2000-01-01T${v2}`) : null);
  return (
    <Modal open title="Tạo đơn" onCancel={onClose} onOk={save} okText="Gửi đơn" okButtonProps={{ disabled: !d.empId || v.errors.length > 0 }} width={560}>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(REQ_KIND) as ReqKind[]).map(k => (
            <button key={k} type="button" onClick={() => patch({ kind: k, assignId: undefined, date: undefined })}
              className={`px-3 py-1 rounded-full border text-xs ${d.kind === k ? 'bg-[#3e91ab] text-white border-[#3e91ab]' : 'bg-white dark:bg-gray-900 border-gray-200 text-gray-600'}`}>{REQ_KIND[k].label}</button>
          ))}
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Nhân sự</div>
          <Select className="w-full" showSearch optionFilterProp="label" placeholder="Chọn nhân sự" value={d.empId || undefined} onChange={empId => patch({ empId, assignId: undefined })}
            options={emps.map(x => ({ value: x.id, label: `${x.name} · ${x.code} · ${branchById(s, x.branchId)?.short}` }))} />
        </div>
        {d.kind === 'leave' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div><div className="text-xs text-gray-500 mb-1">Loại nghỉ</div>
                <Select className="w-full" value={d.leaveKind} onChange={(leaveKind: LeaveKind) => patch({ leaveKind })} options={(Object.keys(LEAVE_KIND) as LeaveKind[]).map(k => ({ value: k, label: LEAVE_KIND[k].label }))} /></div>
              <div><div className="text-xs text-gray-500 mb-1">Số ngày nghỉ</div>
                <InputNumber className="!w-full" min={0.5} step={0.5} value={d.days ?? days} onChange={n => patch({ days: n ?? undefined })} /></div>
            </div>
            <div><div className="text-xs text-gray-500 mb-1">Thời gian</div>
              <DatePicker.RangePicker className="w-full" format="DD/MM/YYYY" value={d.from ? [dayjs(d.from), dayjs(d.to || d.from)] : null}
                onChange={v2 => patch({ from: v2?.[0]?.format('YYYY-MM-DD'), to: v2?.[1]?.format('YYYY-MM-DD'), days: undefined })} /></div>
            {lb && d.leaveKind === 'annual' && <div className="text-xs text-gray-500">Phép năm còn lại: <b className="text-gray-800 dark:text-gray-100">{lb.remain}</b> / {lb.entitled + lb.carried} ngày (đã dùng {lb.used})</div>}
          </>
        )}
        {(d.kind === 'explain' || d.kind === 'swap') && (
          <div><div className="text-xs text-gray-500 mb-1">{d.kind === 'swap' ? 'Ca muốn nhờ người làm thay' : 'Ca cần giải trình'}</div>
            <Select className="w-full" placeholder={d.empId ? 'Chọn ca' : 'Chọn nhân sự trước'} value={d.assignId} onChange={pickAssign} disabled={!d.empId}
              options={myShifts.map(a => { const sh = shiftById(t, a.shiftId)!; return { value: a.id, label: `${dayjs(a.date).format('dd DD/MM')} · ${sh.name} ${shiftLabel(sh)}${a.inAt ? ` · vào ${dayjs(a.inAt).format('HH:mm')}` : ''}${a.outAt ? ` · ra ${dayjs(a.outAt).format('HH:mm')}` : ''}` }; })} />
          </div>
        )}
        {d.kind === 'explain' && (
          <div className="grid grid-cols-2 gap-2">
            <div><div className="text-xs text-gray-500 mb-1">Giờ vào thực tế</div><TimePicker className="w-full" format="HH:mm" minuteStep={5} value={time(d.inTime)} onChange={v2 => patch({ inTime: v2?.format('HH:mm') })} /></div>
            <div><div className="text-xs text-gray-500 mb-1">Giờ ra thực tế</div><TimePicker className="w-full" format="HH:mm" minuteStep={5} value={time(d.outTime)} onChange={v2 => patch({ outTime: v2?.format('HH:mm') })} /></div>
          </div>
        )}
        {d.kind === 'ot' && (
          <div className="grid grid-cols-3 gap-2">
            <div><div className="text-xs text-gray-500 mb-1">Ngày</div><DatePicker className="w-full" format="DD/MM/YYYY" value={d.date ? dayjs(d.date) : null} onChange={v2 => patch({ date: v2?.format('YYYY-MM-DD') })} /></div>
            <div><div className="text-xs text-gray-500 mb-1">Từ</div><TimePicker className="w-full" format="HH:mm" minuteStep={15} value={time(d.otFrom)} onChange={v2 => patch({ otFrom: v2?.format('HH:mm') })} /></div>
            <div><div className="text-xs text-gray-500 mb-1">Đến</div><TimePicker className="w-full" format="HH:mm" minuteStep={15} value={time(d.otTo)} onChange={v2 => patch({ otTo: v2?.format('HH:mm') })} /></div>
            {ot && <div className="col-span-3 text-xs text-gray-500">{ot.hours} giờ làm thêm{ot.night ? `, trong đó ${ot.night} giờ ban đêm (22:00–06:00)` : ''}</div>}
          </div>
        )}
        {d.kind === 'swap' && (
          <div><div className="text-xs text-gray-500 mb-1">Người làm thay</div>
            <Select className="w-full" showSearch optionFilterProp="label" placeholder="Chọn người nhận ca" value={d.toEmpId} onChange={toEmpId => patch({ toEmpId })}
              options={s.employees.filter(x => x.id !== d.empId && isWorking(x, today) && (!e || x.branchId === e.branchId)).map(x => ({ value: x.id, label: `${x.name} · ${positionById(s, x.positionId)?.name}` }))} /></div>
        )}
        <div><div className="text-xs text-gray-500 mb-1">Lý do</div><Input.TextArea rows={2} value={d.reason} onChange={ev => patch({ reason: ev.target.value })} /></div>
        {d.empId && <Notes errors={v.errors.filter(x => x !== 'Nhập lý do')} warnings={v.warnings} />}
      </div>
    </Modal>
  );
}

/** Từ chối đơn / trả bảng lương: hỏi lý do */
export function askReason(title: string, onOk: (note: string) => boolean) {
  let note = '';
  Modal.confirm({
    title, icon: null, okText: 'Xác nhận', cancelText: 'Thôi',
    content: <Input.TextArea autoFocus rows={3} placeholder="Lý do" onChange={ev => { note = ev.target.value; }} />,
    onOk: () => (onOk(note) ? Promise.resolve() : Promise.reject()),
  });
}

/* ============================== Phiếu lương ============================== */

const Line = ({ label, value, strong, minus, sub }: { label: React.ReactNode; value: number; strong?: boolean; minus?: boolean; sub?: boolean }) => (
  <div className={`flex justify-between gap-3 py-1 ${sub ? 'pl-4 text-gray-500 text-xs' : ''} ${strong ? 'font-semibold text-gray-900 dark:text-gray-100' : ''}`}>
    <span>{label}</span><span className={minus && value ? 'text-rose-600' : ''}>{minus && value ? '−' : ''}{fmtMoney(value)}</span>
  </div>
);

export function PayslipView({ p, r }: { p: PayPeriod; r: PayRow }) {
  const s = useHr();
  const t = getTp();
  return (
    <div className="text-sm space-y-3">
      <div className="flex justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-2">
        <div>
          <div className="text-base font-semibold">{r.name}</div>
          <div className="text-xs text-gray-500">{r.code} · {r.position} · {branchById(s, r.branchId)?.short}</div>
          <div className="text-xs text-gray-500">{r.kind === 'none' ? 'Chưa có HĐ' : CONTRACT_KIND[r.kind].label} · lương {fmtMoney(r.salary)}</div>
        </div>
        <div className="text-right text-xs text-gray-500 pr-7">
          <div className="font-semibold text-gray-800 dark:text-gray-100">Phiếu lương {fmtMonth(p.month)}</div>
          <div>Ngày trả: {fmtD(dayjs(`${p.month}-01`).add(1, 'month').date(t.pay.payDay).format('YYYY-MM-DD'))}</div>
          {r.sentAt && <div className="text-emerald-600">Đã gửi {dayjs(r.sentAt).format('DD/MM HH:mm')}</div>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 py-2"><div className="text-gray-500">Ngày hưởng lương</div><b className="text-base">{r.paidDays}</b> / {r.std}</div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 py-2"><div className="text-gray-500">Công / phép / lễ</div><b>{r.work} · {r.paidLeave} · {r.holiday}</b></div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 py-2"><div className="text-gray-500">Làm thêm · muộn/sớm</div><b>{r.otH}h · {r.lateMin}′</b></div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        <div className="pb-1">
          <div className="text-xs font-semibold text-gray-500 uppercase py-1">Thu nhập</div>
          <Line label={`Lương theo ngày công (${r.paidDays}/${r.std})`} value={r.basePay} />
          {r.lateDeduct > 0 && <Line label={`Trừ thời gian đi muộn / về sớm (${r.lateMin} phút)`} value={r.lateDeduct} minus sub />}
          {r.otPay > 0 && <Line label={`Làm thêm giờ / làm ngày lễ (${r.otH} giờ)`} value={r.otPay} />}
          {r.nightPay > 0 && <Line label="Phụ cấp làm đêm" value={r.nightPay} />}
          {r.allowances.map(a => <Line key={a.name} label={`Phụ cấp ${a.name.toLowerCase()}`} value={a.amount} />)}
          {r.bonus > 0 && <Line label="Thưởng" value={r.bonus} />}
          {r.commission > 0 && <Line label="Hoa hồng / doanh số" value={r.commission} />}
          {r.arrear > 0 && <Line label="Truy lĩnh" value={r.arrear} />}
          {r.leavePayout > 0 && <Line label="Thanh toán phép năm chưa nghỉ" value={r.leavePayout} />}
          <Line label="Tổng thu nhập" value={r.gross} strong />
        </div>
        <div className="py-1">
          <div className="text-xs font-semibold text-gray-500 uppercase py-1">Khấu trừ</div>
          <Line label={`BHXH ${t.pay.emp.bhxh}% · BHYT ${t.pay.emp.bhyt}% · BHTN ${t.pay.emp.bhtn}%${r.insBase ? ` (trên ${fmtMoney(r.insBase)})` : ''}`} value={r.insEmpTotal} minus />
          <Line label={r.pitMode === 'flat' ? `Thuế TNCN khấu trừ ${t.pay.flatRate}%` : `Thuế TNCN${r.pitMode === 'progressive' ? ` (giảm trừ ${r.dependents} người phụ thuộc)` : ''}`} value={r.pit} minus />
          {r.advance > 0 && <Line label="Tạm ứng" value={r.advance} minus />}
          {r.otherDeduct > 0 && <Line label="Khấu trừ khác" value={r.otherDeduct} minus />}
        </div>
        <div className="pt-2 flex justify-between items-center">
          <span className="font-semibold">Thực nhận</span>
          <span className="text-xl font-bold text-[#3e91ab]">{fmtMoney(r.net)}</span>
        </div>
      </div>
      {r.bank && <div className="text-xs text-gray-500">Chuyển khoản: {r.bank.bank} · {r.bank.account}</div>}
      {r.notes.length > 0 && <div className="text-xs space-y-0.5">{r.notes.map(n => <div key={n} className="text-amber-700">⚠ {n}</div>)}</div>}
    </div>
  );
}
